import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Download,
  Pencil,
  Lightbulb,
  Home,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  CreditCard,
  Eye,
  Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { SupabasePreApprovalService } from '@/services/supabasePreApprovalService';
import { PreApprovalData } from '@/types/pre-approval';
import { formatCurrency, formatPercentage } from '@/utils/formatCurrency';
import { formatLoanType } from '@/utils/pre-approval/formatters';
import { downloadVisualPreApprovalPDF } from '@/utils/pre-approval/visualTemplatePdfGenerator';
import { UserService } from '@/services';
import { useIsMobile } from '@/hooks/use-mobile';
import { PRE_APPROVAL_CONSTANTS } from '@/constants/pre-approval';
import {
  formatPreApprovalExpirationDate,
  resolveCanonicalPreApprovalExpiresAt
} from '@/utils/pre-approval/expiration';
import { VisualTemplatePreview } from './VisualTemplatePreview';

import logger from '@/utils/logger';
interface TemplateElement {
  id: string;
  type: 'text' | 'image' | 'divider' | 'signature';
  content?: string;
  imageUrl?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  styles: {
    fontSize: number;
    fontFamily: string;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
    textDecoration: 'none' | 'underline';
    textAlign: 'left' | 'center' | 'right';
    color: string;
    backgroundColor?: string;
    padding: number;
    borderRadius: number;
    border?: string;
    lineHeight?: number;
  };
}

interface TemplateLayout {
  id: string;
  name: string;
  pageSize: 'letter' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  elements: TemplateElement[];
  createdAt: string;
  updatedAt: string;
}

export const PreApprovalBorrowerPortal: React.FC = () => {
  const { token, id } = useParams<{ token?: string; id?: string }>();
  const [searchParams] = useSearchParams();
  const isLoanOfficerPreview = searchParams.get('lo') === 'true';
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<'letter' | 'controls'>('letter');

  // Use id param for preview route, token param for public access
  const preApprovalIdentifier = id || token;
  const [preApproval, setPreApproval] = useState<PreApprovalData | null>(null);
  const [visualTemplate, setVisualTemplate] = useState<TemplateLayout | null>(null);
  const [loading, setLoading] = useState(true);
  // Two sources fill this (public links' sanitized variables, or the loan officer's own), read by name below.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [profileVariables, setProfileVariables] = useState<Record<string, any> | null>(null);

  // Interactive fields
  const [purchasePrice, setPurchasePrice] = useState(0);
  const [adjustedLoanAmount, setAdjustedLoanAmount] = useState(0);
  
  // Input field states
  const [loanAmountInput, setLoanAmountInput] = useState('');
  const [purchasePriceInput, setPurchasePriceInput] = useState('');
  
  // Calculated values - down payment is now always calculated
  const downPaymentAmount = purchasePrice - adjustedLoanAmount;
  const downPaymentPercent = purchasePrice > 0 ? Math.round((downPaymentAmount / purchasePrice) * 100 * 10) / 10 : 0;
  const [ltv, setLtv] = useState(0);

  // Original values for constraints
  const [originalLoanAmount, setOriginalLoanAmount] = useState(0);
  const [maxPurchasePrice, setMaxPurchasePrice] = useState(0);
  const [maxLTV, setMaxLTV] = useState(100); // Maximum LTV approved by loan officer
  
  // Zoom controls
  const [zoomLevel, setZoomLevel] = useState(PRE_APPROVAL_CONSTANTS.PDF_ZOOM.DEFAULT); // Start at 110% for better readability
  const minZoom = PRE_APPROVAL_CONSTANTS.PDF_ZOOM.MIN;
  const maxZoom = PRE_APPROVAL_CONSTANTS.PDF_ZOOM.MAX;

  // Track if user has seen/clicked the Customize tab (for glow animation)
  const [hasSeenCustomize, setHasSeenCustomize] = useState(false);

  // Initialize hasSeenCustomize from localStorage once we have the identifier
  useEffect(() => {
    if (preApprovalIdentifier) {
      const seen = localStorage.getItem(`preapproval-customize-seen-${preApprovalIdentifier}`) === 'true';
      setHasSeenCustomize(seen);
    }
  }, [preApprovalIdentifier]);

  // Handle tab change - track when user clicks Customize tab
  const handleTabChange = (value: string) => {
    setActiveTab(value as 'letter' | 'controls');
    if (value === 'controls' && !hasSeenCustomize && preApprovalIdentifier) {
      setHasSeenCustomize(true);
      localStorage.setItem(`preapproval-customize-seen-${preApprovalIdentifier}`, 'true');
    }
  };

  // Helper function to format numbers with thousand separators
  const formatNumberWithCommas = (value: string): string => {
    if (!value) {
return '';
}
    // Remove any existing commas and non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    // Add thousand separators
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  useEffect(() => {
    if (preApprovalIdentifier) {
      loadPreApproval(preApprovalIdentifier);
    }
  }, [preApprovalIdentifier]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPreApproval = async (id: string) => {
    try {
      const data = isLoanOfficerPreview
        ? await SupabasePreApprovalService.getPreApprovalById(id)
        : await SupabasePreApprovalService.getPublicAccessiblePreApproval(id);

      if (data && data.loanDetails && data.borrowerInfo) {
        setPreApproval(data);

        const publicProfileVariables = !isLoanOfficerPreview ? data.publicProfileVariables : null;

        // Public links receive sanitized profile variables from the edge resolver.
        if (publicProfileVariables) {
          setProfileVariables(publicProfileVariables);
          logger.info('Public loan officer profile variables loaded successfully', {
            hasName: !!publicProfileVariables.loanOfficerName,
            hasCompany: !!publicProfileVariables.companyName
          });
        } else if (data.loanOfficerId) {
          try {
            const variables = await UserService.getTemplateVariablesForUser(data.loanOfficerId);
            setProfileVariables(variables);

            // Log successful profile load for debugging
            logger.info('Loan officer profile loaded successfully', {
              loanOfficerId: data.loanOfficerId,
              hasName: !!variables.loanOfficerName,
              hasCompany: !!variables.companyName,
              hasCompanyAddress: !!variables.companyAddress,
              companyAddressType: typeof variables.companyAddress
            });
          } catch (profileError) {
            logger.error('Failed to load loan officer profile:', { loanOfficerId: data.loanOfficerId }, profileError);
            // Continue without profile data - will use fallbacks
          }
        } else {
          logger.warn('Pre-approval missing public profile variables, cannot load loan officer profile');
        }

        // Load visual template from the pre-approval data
        if (data.template_data) {
          setVisualTemplate(data.template_data);
        } else {
          // Fallback to localStorage for backwards compatibility
          const templateData = localStorage.getItem(`visual-template-${id}`);
          if (templateData) {
            const parsedTemplate = JSON.parse(templateData);
            setVisualTemplate(parsedTemplate);
          }
        }
        
        // Initialize values
        const loanAmount = data.loanDetails?.loanAmount || 0;
        const initialPurchasePrice = data.loanDetails?.purchasePrice || loanAmount;

        // Calculate the maximum LTV that was approved
        const approvedLTV = initialPurchasePrice > 0 ? (loanAmount / initialPurchasePrice) * 100 : 0;

        setOriginalLoanAmount(loanAmount);
        setAdjustedLoanAmount(loanAmount);
        setPurchasePrice(initialPurchasePrice);
        setMaxLTV(approvedLTV);

        // Initialize input fields
        setLoanAmountInput(loanAmount.toString());
        setPurchasePriceInput(initialPurchasePrice.toString());
        
        // Set constraints - max purchase price should be what the loan officer approved
        setMaxPurchasePrice(initialPurchasePrice); // Cannot go higher than approved amount

        // Track view (skip if loan officer preview)
        if (!isLoanOfficerPreview) {
          await SupabasePreApprovalService.trackView(id);
        }
      }
    } catch (error) {
      logger.error('Failed to load pre-approval:', error);
      toast.error('Failed to load pre-approval');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate values when inputs change
  useEffect(() => {
    if (!preApproval) {
return;
}
    
    // Calculate LTV using adjusted loan amount
    const newLtv = purchasePrice > 0 ? (adjustedLoanAmount / purchasePrice) * 100 : 0;
    setLtv(Math.round(newLtv * 10) / 10);
  }, [purchasePrice, adjustedLoanAmount, preApproval]);

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case '=':
          case '+':
            e.preventDefault();
            setZoomLevel(prev => Math.min(maxZoom, prev + 0.1));
            break;
          case '-':
            e.preventDefault();
            setZoomLevel(prev => Math.max(minZoom, prev - 0.1));
            break;
          case '0':
            e.preventDefault();
            setZoomLevel(1);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [minZoom, maxZoom]);

  const handlePurchasePriceChange = (value: number[]) => {
    const newPrice = value[0];
    setPurchasePrice(newPrice);
    setPurchasePriceInput(newPrice.toString());
    
    // Calculate what the loan amount should be to maintain max LTV
    const maxLoanAmountForLTV = (newPrice * maxLTV) / 100;
    
    // If the current loan amount would exceed max LTV, adjust it down
    if (adjustedLoanAmount > maxLoanAmountForLTV) {
      setAdjustedLoanAmount(Math.floor(maxLoanAmountForLTV));
      setLoanAmountInput(Math.floor(maxLoanAmountForLTV).toString());
    }
    
    // Also ensure loan amount doesn't exceed purchase price
    if (newPrice < adjustedLoanAmount) {
      setAdjustedLoanAmount(newPrice);
      setLoanAmountInput(newPrice.toString());
    }
  };

  const handleLoanAmountChange = (value: number[]) => {
    const newLoanAmount = value[0];
    setAdjustedLoanAmount(newLoanAmount);
    setLoanAmountInput(newLoanAmount.toString());
    
    // Adjust purchase price if it's less than the new loan amount
    if (purchasePrice < newLoanAmount) {
      setPurchasePrice(newLoanAmount);
      setPurchasePriceInput(newLoanAmount.toString());
    }
  };

  const handleDownloadPDF = () => {
    if (!preApproval) {
      toast.error('Pre-approval data not loaded');
      return;
    }
    
    if (!visualTemplate) {
      toast.error('Pre-approval template not found. Please contact your loan officer.');
      return;
    }
    
    // Create updated data for PDF
    const updatedData: PreApprovalData = {
      ...preApproval,
      loanDetails: {
        ...preApproval.loanDetails,
        loanAmount: adjustedLoanAmount,
        purchasePrice,
        downPaymentAmount,
        downPaymentPercent
      },
      calculations: {
        ...preApproval.calculations,
        ltv
      }
    };

    try {
      downloadVisualPreApprovalPDF(
        visualTemplate,
        updatedData,
        isLoanOfficerPreview ? preApproval.loanOfficerId : undefined,
        profileVariables || preApproval.publicProfileVariables
      );
    } catch (error) {
      logger.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const generatePDFPreview = useCallback(() => {
    if (!preApproval || !visualTemplate) {
      return (
        <div className="w-full h-full bg-white shadow-lg rounded-lg p-8 overflow-auto">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-gray-500">Template not found. Please contact your loan officer.</p>
          </div>
        </div>
      );
    }

    // Use zoomLevel for both mobile and desktop - user controls the zoom
    const effectiveScale = zoomLevel;

    return (
      <div className="w-full min-h-full flex items-start justify-center bg-gray-100 p-4">
        <VisualTemplatePreview
          template={visualTemplate}
          formData={{
            // Borrower info
            borrowerSalutation: preApproval.borrowerInfo?.salutation,
            borrowerFirstName: preApproval.borrowerInfo?.firstName,
            borrowerLastName: preApproval.borrowerInfo?.lastName,
            borrowerEmail: preApproval.borrowerInfo?.email,
            borrowerPhone: preApproval.borrowerInfo?.phone,
            coBorrowerSalutation: preApproval.coBorrowerInfo?.salutation,
            coBorrowerFirstName: preApproval.coBorrowerInfo?.firstName,
            coBorrowerLastName: preApproval.coBorrowerInfo?.lastName,
            coBorrowerEmail: preApproval.coBorrowerInfo?.email,
            coBorrowerPhone: preApproval.coBorrowerInfo?.phone,

            // Property info
            propertyAddress: preApproval.propertyInfo?.address,
            propertyStreet: preApproval.propertyInfo?.address,
            propertyCity: preApproval.propertyInfo?.city,
            propertyState: preApproval.propertyInfo?.state,
            propertyZip: preApproval.propertyInfo?.zipCode,
            propertyType: preApproval.propertyInfo?.propertyType,
            propertyOccupancy: preApproval.propertyInfo?.occupancy,

            // Loan details (using adjusted values)
            loanAmount: adjustedLoanAmount?.toString(),
            purchasePrice: purchasePrice?.toString(),
            loanType: preApproval.loanDetails?.loanType,
            downPayment: downPaymentAmount?.toString(),
            downPaymentPercent: downPaymentPercent?.toString(),
            ltv: ltv?.toString(),

            // Dates
            expirationDate: formatPreApprovalExpirationDate(
              resolveCanonicalPreApprovalExpiresAt(preApproval.expiresAt, preApproval.expirationDate)
            ) || preApproval.expirationDate,
            currentDate: new Date().toISOString(),
            dateOfPreApproval: new Date().toISOString(),

            // Loan officer info (from profileVariables)
            loanOfficerName: profileVariables?.loanOfficerName,
            loanOfficerFirstName: profileVariables?.loanOfficerFirstName,
            loanOfficerLastName: profileVariables?.loanOfficerLastName,
            loanOfficerTitle: profileVariables?.loanOfficerTitle,
            loanOfficerNMLS: profileVariables?.loanOfficerNMLS,
            loanOfficerPhone: profileVariables?.loanOfficerPhone,
            loanOfficerMobile: profileVariables?.loanOfficerMobile,
            loanOfficerEmail: profileVariables?.loanOfficerEmail,
            loanOfficerWebsite: profileVariables?.companyWebsite,

            // Company info
            companyName: profileVariables?.companyName,
            companyNMLS: profileVariables?.companyNMLS,
            companyEmail: profileVariables?.companyEmail,
            companyWebsite: profileVariables?.companyWebsite,
            companyPhone: profileVariables?.companyPhone,
            companyAddress: profileVariables?.companyAddress ?
              `${profileVariables.companyAddress.street || ''}, ${profileVariables.companyAddress.city || ''}, ${profileVariables.companyAddress.state || ''} ${profileVariables.companyAddress.zip || ''}`.replace(/,\s*,/g, ',').trim()
              : undefined,
            companyStreet: profileVariables?.companyAddress?.street || undefined,
            companyCity: profileVariables?.companyAddress?.city || undefined,
            companyState: profileVariables?.companyAddress?.state || undefined,
            companyZip: profileVariables?.companyAddress?.zip || undefined
          }}
          loanOfficerSignatureDataUrl={preApproval.loanOfficerSignatureDataUrl}
          hasLoanOfficerSignature={preApproval.hasLoanOfficerSignature || !!preApproval.loanOfficerSignatureDataUrl}
          loanOfficerSignatureDate={preApproval.loanOfficerSignatureDate}
          loanOfficerSignatureIpAddress={preApproval.loanOfficerSignatureIpAddress}
          hideControls={true}
          externalScale={effectiveScale}
          className="shadow-2xl"
        />
      </div>
    );
  }, [preApproval, visualTemplate, zoomLevel, adjustedLoanAmount, purchasePrice, downPaymentAmount, downPaymentPercent, ltv, profileVariables]);

  if (loading) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#183C2E] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your pre-approval...</p>
        </div>
      </div>
    );
  }

  if (!preApproval) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center bg-gray-50">
        <Card className="p-8 max-w-md">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Pre-Approval Not Found</h2>
            <p className="text-gray-600">
              This pre-approval may have expired or been removed.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Mobile Tab Layout
  if (isMobile) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col min-h-0">
          <TabsList className="!flex w-full h-14 rounded-none border-b bg-white p-0">
            <TabsTrigger value="letter" className="flex-1 text-base rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#183C2E]">
              <FileText className="w-4 h-4 mr-2" />
              Letter
            </TabsTrigger>
            <TabsTrigger
              value="controls"
              className={cn(
                "flex-1 text-base rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#183C2E]",
                activeTab !== 'controls' && "border-2 border-[#183C2E]",
                !hasSeenCustomize && activeTab !== 'controls' && "animate-tab-glow"
              )}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Customize
            </TabsTrigger>
          </TabsList>

          {/* Letter Tab - Full Screen PDF */}
          <TabsContent value="letter" className="min-h-0 flex flex-col bg-white" style={{ marginTop: 0 }}>
            {/* Sticky Header with Zoom Controls */}
            <div className="sticky top-0 z-10 bg-white border-b px-4 py-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">Pre-Approval Letter</h3>
                <div className="flex gap-1">
                  <Button
                    onClick={() => setZoomLevel(Math.max(minZoom, zoomLevel - 0.1))}
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 p-0"
                    disabled={zoomLevel <= minZoom}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-2 flex items-center min-w-[3rem] justify-center">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <Button
                    onClick={() => setZoomLevel(Math.min(maxZoom, zoomLevel + 0.1))}
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 p-0"
                    disabled={zoomLevel >= maxZoom}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => setZoomLevel(1)}
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 p-0"
                    title="Reset zoom"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleDownloadPDF}
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 p-0"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            {/* PDF Preview */}
            <div className="flex-1 overflow-auto">
              {generatePDFPreview()}
            </div>
          </TabsContent>

          {/* Controls Tab - Full Screen Form */}
          <TabsContent value="controls" className="flex-1 min-h-0 flex flex-col bg-white" style={{ marginTop: 0 }}>
            <div className="h-0 flex-1 overflow-y-auto space-y-6 px-4 pb-24">
              {/* Loan Summary */}
              <Card className="border-2 border-[#183C2E]">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-center gap-2 text-lg">
                    <Calculator className="w-5 h-5" />
                    Loan Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-base">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Original Pre-Approval:</span>
                    <span className="font-semibold">{formatCurrency(originalLoanAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Loan Amount:</span>
                    <span className="font-semibold text-[#183C2E]">{formatCurrency(adjustedLoanAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">LTV Ratio:</span>
                    <span className="font-semibold flex items-center gap-2">
                      {formatPercentage(ltv, 1)}
                      {ltv > 80 && (
                        <Badge variant="secondary" className="text-xs">PMI Required</Badge>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Loan Type:</span>
                    <span className="font-semibold">{formatLoanType(preApproval.loanDetails.loanType || '')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Down Payment:</span>
                    <span className="font-semibold">
                      {formatCurrency(downPaymentAmount)}
                      <span className="text-sm text-gray-600 ml-1">({downPaymentPercent.toFixed(1)}%)</span>
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Alert className="py-3 bg-amber-50 border-amber-200">
                <Lightbulb className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-800">
                  <span className="font-medium">Making an offer?</span> Adjust your letter to match your offer amount—protect your negotiating position.
                </AlertDescription>
              </Alert>

              {/* Loan Amount Slider */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-center gap-2 text-lg">
                    <CreditCard className="w-5 h-5" />
                    Amount on Letter
                  </CardTitle>
                  <p className="text-sm text-gray-500 text-center">
                    Your approval: {formatCurrency(originalLoanAmount)}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#183C2E] mr-1">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithCommas(loanAmountInput)}
                      onChange={(e) => {
                        const cleanValue = e.target.value.replace(/[^0-9]/g, '');
                        setLoanAmountInput(cleanValue);
                      }}
                      onBlur={() => {
                        const numValue = parseInt(loanAmountInput) || 0;
                        const min = Math.min(100000, originalLoanAmount * 0.5);
                        const max = Math.min(originalLoanAmount, Math.floor((purchasePrice * maxLTV) / 100));

                        let finalValue = numValue;
                        if (numValue < min) {
                          finalValue = min;
                        } else if (numValue > max) {
                          finalValue = max;
                        }

                        handleLoanAmountChange([finalValue]);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        }
                      }}
                      className="text-2xl font-bold text-[#183C2E] bg-transparent border-b-2 border-gray-300 focus:border-[#183C2E] outline-none text-center w-36"
                    />
                  </div>
                  <Slider
                    value={[adjustedLoanAmount]}
                    onValueChange={handleLoanAmountChange}
                    min={Math.min(PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_AMOUNT, originalLoanAmount * PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_PERCENT)}
                    max={Math.min(originalLoanAmount, Math.floor((purchasePrice * maxLTV) / 100))}
                    step={PRE_APPROVAL_CONSTANTS.SLIDER.LOAN_AMOUNT_STEP}
                    className="w-full [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&>span]:h-3"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{formatCurrency(Math.min(PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_AMOUNT, originalLoanAmount * PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_PERCENT))}</span>
                    <span>{formatCurrency(Math.min(originalLoanAmount, Math.floor((purchasePrice * maxLTV) / 100)))}</span>
                  </div>
                  {adjustedLoanAmount < originalLoanAmount && (
                    <p className="text-sm text-amber-600">
                      Loan amount reduced by {formatCurrency(originalLoanAmount - adjustedLoanAmount)}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Purchase Price Slider */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-center gap-2 text-lg">
                    <Home className="w-5 h-5" />
                    Purchase Price
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#183C2E] mr-1">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithCommas(purchasePriceInput)}
                      onChange={(e) => {
                        const cleanValue = e.target.value.replace(/[^0-9]/g, '');
                        setPurchasePriceInput(cleanValue);
                      }}
                      onBlur={() => {
                        const numValue = parseInt(purchasePriceInput) || 0;
                        const min = adjustedLoanAmount;
                        const max = maxPurchasePrice;

                        let finalValue = numValue;
                        if (numValue < min) {
                          finalValue = min;
                        } else if (numValue > max) {
                          finalValue = max;
                        }

                        handlePurchasePriceChange([finalValue]);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        }
                      }}
                      className="text-2xl font-bold text-[#183C2E] bg-transparent border-b-2 border-gray-300 focus:border-[#183C2E] outline-none text-center w-36"
                    />
                  </div>
                  <Slider
                    value={[purchasePrice]}
                    onValueChange={handlePurchasePriceChange}
                    min={adjustedLoanAmount}
                    max={maxPurchasePrice}
                    step={PRE_APPROVAL_CONSTANTS.SLIDER.LOAN_AMOUNT_STEP}
                    className="w-full [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&>span]:h-3"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{formatCurrency(adjustedLoanAmount)}</span>
                    <span>{formatCurrency(maxPurchasePrice)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Preview Changes Button */}
              <Button
                onClick={() => setActiveTab('letter')}
                variant="outline"
                className="w-full h-12 text-base"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Changes on Letter
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Desktop Two-Column Layout
  return (
    <>
      <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Left Side - PDF Preview */}
      <div className="flex-1 p-6">
        <div className="h-[calc(100vh-3rem)] bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="border-b p-4 flex items-center justify-between bg-gray-50">
            <h2 className="text-lg font-semibold">Pre-Approval Letter Preview</h2>
            <div className="flex items-center gap-2">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 bg-white rounded-lg border px-2 py-1">
                <Button
                  onClick={() => setZoomLevel(Math.max(minZoom, zoomLevel - 0.1))}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={zoomLevel <= minZoom}
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium px-2 min-w-[3rem] text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                  onClick={() => setZoomLevel(Math.min(maxZoom, zoomLevel + 0.1))}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={zoomLevel >= maxZoom}
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <div className="w-px h-4 bg-gray-300 mx-1" />
                <Button
                  onClick={() => setZoomLevel(1)}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  title="Reset zoom"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
              <Button onClick={handleDownloadPDF} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                {adjustedLoanAmount < originalLoanAmount ? 'Download Customized Letter' : 'Download Letter'}
              </Button>
            </div>
          </div>
          <div className="p-4 h-[calc(100%-4rem)] overflow-auto">
            {generatePDFPreview()}
          </div>
        </div>
      </div>

      {/* Right Side - Interactive Controls */}
      <div className="w-96 bg-white shadow-xl p-6 overflow-y-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">Customize Your Letter</h1>
          <p className="text-gray-600">
            Adjust amounts for your offer—no need to reveal your full approval to the seller.
          </p>
        </div>

        {/* Loan Summary - Moved to Top */}
        <Card className="mb-4 border-2 border-[#183C2E]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-center gap-2 text-lg">
              <Calculator className="w-4 h-4" />
              Loan Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Original Pre-Approval:</span>
              <span className="font-semibold">{formatCurrency(originalLoanAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Current Loan Amount:</span>
              <span className="font-semibold text-[#183C2E]">{formatCurrency(adjustedLoanAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">LTV Ratio:</span>
              <span className="font-semibold flex items-center gap-2">
                {formatPercentage(ltv, 1)}
                {ltv > 80 && (
                  <Badge variant="secondary" className="text-xs">PMI Required</Badge>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Loan Type:</span>
              <span className="font-semibold">{formatLoanType(preApproval.loanDetails.loanType || '')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Down Payment:</span>
              <span className="font-semibold">
                {formatCurrency(downPaymentAmount)}
                <span className="text-sm text-gray-600 ml-1">({downPaymentPercent.toFixed(1)}%)</span>
              </span>
            </div>
          </CardContent>
        </Card>


        <Alert className="mb-4 py-3 bg-amber-50 border-amber-200">
          <Lightbulb className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <span className="font-medium">Making an offer?</span> Adjust your letter to match your offer amount—protect your negotiating position.
          </AlertDescription>
        </Alert>

        {/* Loan Amount Slider */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-center gap-2 text-lg">
              <CreditCard className="w-4 h-4" />
              Amount on Letter
            </CardTitle>
            <p className="text-sm text-gray-500 text-center">
              Your approval: {formatCurrency(originalLoanAmount)}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-center">
              <span className="text-2xl font-bold text-[#183C2E] mr-1">$</span>
              <input
                type="text"
                value={formatNumberWithCommas(loanAmountInput)}
                onChange={(e) => {
                  // Allow user to type freely, just clean non-numeric characters
                  const cleanValue = e.target.value.replace(/[^0-9]/g, '');
                  setLoanAmountInput(cleanValue);
                }}
                onBlur={() => {
                  // Apply constraints only when user is done typing
                  const numValue = parseInt(loanAmountInput) || 0;
                  const min = Math.min(100000, originalLoanAmount * 0.5);
                  const max = Math.min(originalLoanAmount, Math.floor((purchasePrice * maxLTV) / 100));

                  let finalValue = numValue;
                  if (numValue < min) {
                    finalValue = min;
                  } else if (numValue > max) {
                    finalValue = max;
                  }

                  handleLoanAmountChange([finalValue]);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className="text-2xl font-bold text-[#183C2E] bg-transparent border-b-2 border-gray-300 focus:border-[#183C2E] outline-none text-center w-32"
              />
            </div>
            <Slider
              value={[adjustedLoanAmount]}
              onValueChange={handleLoanAmountChange}
              min={Math.min(PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_AMOUNT, originalLoanAmount * PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_PERCENT)} // Allow reduction up to 50%
              max={Math.min(originalLoanAmount, Math.floor((purchasePrice * maxLTV) / 100))} // Cannot exceed original approval or max LTV
              step={PRE_APPROVAL_CONSTANTS.SLIDER.LOAN_AMOUNT_STEP}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>{formatCurrency(Math.min(PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_AMOUNT, originalLoanAmount * PRE_APPROVAL_CONSTANTS.SLIDER.MIN_LOAN_PERCENT))}</span>
              <span>{formatCurrency(Math.min(originalLoanAmount, Math.floor((purchasePrice * maxLTV) / 100)))}</span>
            </div>
            {adjustedLoanAmount < originalLoanAmount && (
              <p className="text-sm text-amber-600">
                Loan amount reduced by {formatCurrency(originalLoanAmount - adjustedLoanAmount)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Purchase Price Slider */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-center gap-2 text-lg">
              <Home className="w-4 h-4" />
              Purchase Price
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-center">
              <span className="text-2xl font-bold text-[#183C2E] mr-1">$</span>
              <input
                type="text"
                value={formatNumberWithCommas(purchasePriceInput)}
                onChange={(e) => {
                  // Allow user to type freely, just clean non-numeric characters
                  const cleanValue = e.target.value.replace(/[^0-9]/g, '');
                  setPurchasePriceInput(cleanValue);
                }}
                onBlur={() => {
                  // Apply constraints only when user is done typing
                  const numValue = parseInt(purchasePriceInput) || 0;
                  const min = adjustedLoanAmount;
                  const max = maxPurchasePrice;

                  let finalValue = numValue;
                  if (numValue < min) {
                    finalValue = min;
                  } else if (numValue > max) {
                    finalValue = max;
                  }

                  handlePurchasePriceChange([finalValue]);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className="text-2xl font-bold text-[#183C2E] bg-transparent border-b-2 border-gray-300 focus:border-[#183C2E] outline-none text-center w-32"
              />
            </div>
            <Slider
              value={[purchasePrice]}
              onValueChange={handlePurchasePriceChange}
              min={adjustedLoanAmount} // Can't go below loan amount
              max={maxPurchasePrice}
              step={PRE_APPROVAL_CONSTANTS.SLIDER.LOAN_AMOUNT_STEP}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>{formatCurrency(adjustedLoanAmount)}</span>
              <span>{formatCurrency(maxPurchasePrice)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};
